import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axios";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import "react-confirm-alert/src/react-confirm-alert.css";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import Pagination from "../../components/AdminSideComponents/AdminPagination";
import DeleteConfirmationModal from "../../components/AdminSideComponents/Modals/DeleteConfirmationModal";
import toast from "react-hot-toast";
import AdminBlogModal from "../../components/AdminSideComponents/Modals/AdminBlogModal";

const AdminBlogs = () => {
  const ITEMS_PER_PAGE = 8;

  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletingBlogId, setDeletingBlogId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // FETCH BLOGS
  const fetchBlogs = async () => {
    try {
      const response = await axiosInstance.get("/blogs/get-all");
      setBlogs(response.data.reverse());
      setFilteredBlogs(response.data);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      console.error("Error fetching blogs:", err);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    const newTotalPages = Math.ceil(filteredBlogs.length / ITEMS_PER_PAGE);
    if (currentPage > newTotalPages) {
      setCurrentPage(newTotalPages || 1);
    }
  }, [filteredBlogs, currentPage]);

  // SEARCH HANDLER
  const onSearchHandler = (query) => {
    setSearchTerm(query);
    const lowerCaseQuery = query.trim().toLowerCase();
    const results = blogs.filter(
      (blog) =>
        blog.title.toLowerCase().includes(lowerCaseQuery) ||
        blog.author.toLowerCase().includes(lowerCaseQuery)
    );
    setFilteredBlogs(results);
    setCurrentPage(1);
  };

  // DELETE HANDLERS
  const handleOpenDeleteModal = (id) => {
    setDeletingBlogId(id);
    setIsDeleteModalOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setDeletingBlogId(null);
  };

  const handleConfirmDelete = async () => {
    if (!deletingBlogId) return;
    try {
      setIsDeleting(true);
      await axiosInstance.delete(`/blogs/delete/${deletingBlogId}`);
      const updatedList = blogs.filter((blog) => blog._id !== deletingBlogId);
      setBlogs(updatedList);
      setFilteredBlogs(
        searchTerm
          ? updatedList.filter(
              (blog) =>
                blog.title
                  .toLowerCase()
                  .includes(searchTerm.trim().toLowerCase()) ||
                blog.author
                  .toLowerCase()
                  .includes(searchTerm.trim().toLowerCase())
            )
          : updatedList
      );
      toast.success("Blog deleted successfully", { id: deletingBlogId });
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error("Failed to delete. Try again.");
    } finally {
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setDeletingBlogId(null);
    }
  };

  const onAddNewHandler = () => {
    setEditData(null);
    setModalOpen(true);
  };

  // HANDLE EDIT CLICK
  const handleEditClick = (blog) => {
    setEditData(blog);
    setModalOpen(true);
  };

  // HANDLE BLOG SUBMISSION (CREATE/UPDATE)
  const handleBlogSubmit = async (blogData) => {
    setIsSubmitting(true);
    try {
      if (editData) {
        // Update existing blog
        const updatedBlog = await axiosInstance.put(
          `/blogs/update/${editData._id}`,
          blogData
        );
        setBlogs(
          blogs.map((b) => (b._id === editData._id ? updatedBlog.data : b))
        );
        setFilteredBlogs(
          filteredBlogs.map((b) =>
            b._id === editData._id ? updatedBlog.data : b
          )
        );
        toast.success("Blog updated successfully");
      } else {
        // Create new blog
        const newBlog = await axiosInstance.post("/blogs/create", blogData);
        setBlogs([newBlog.data, ...blogs]);
        setFilteredBlogs([newBlog.data, ...filteredBlogs]);
        toast.success("Blog created successfully");
      }
      setModalOpen(false);
    } catch (err) {
      console.error("Error saving blog:", err);
      toast.error(err.response?.data?.message || "Failed to save blog");
    } finally {
      setIsSubmitting(false);
      fetchBlogs();
    }
  };

  // PAGINATION CALC
  const totalPages = Math.ceil(filteredBlogs.length / ITEMS_PER_PAGE);
  const paginatedBlogs = filteredBlogs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden animate-pulse">
      <div className="h-48 bg-gray-200"></div>
      <div className="p-4">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
      </div>
    </div>
  );

  // Format date helper
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <AdminSearchBar
          title="Blogs Management"
          onSearchClick={onSearchHandler}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-4">
          {Array.from({ length: ITEMS_PER_PAGE }).map((_, idx) => (
            <SkeletonCard key={idx} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <AdminSearchBar
          title="Blogs Management"
          onSearchClick={onSearchHandler}
        />
        <div className="mt-6 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg flex items-center justify-between">
          <div>
            <p className="font-semibold">Failed to load blogs</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
          <button
            onClick={fetchBlogs}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium transition"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 px-4 pr-4">
      {/* SEARCH BAR */}
      <AdminSearchBar
        title="Blogs Management"
        onSearchClick={onSearchHandler}
        onAddNewClick={onAddNewHandler}
      />

      {paginatedBlogs.length === 0 ? (
        <div className="text-center py-12">
          <h3 className="text-lg font-medium text-gray-600">No blogs found</h3>
          <p className="text-gray-500 mt-2">
            Click the button above to add your first blog
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {paginatedBlogs.map((blog) => (
            <div
              key={blog._id}
              className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition duration-300 flex flex-col relative"
            >
              {/* IMAGE */}
              <div className="relative h-48 bg-gray-100">
                {blog.image ? (
                  <img
                    src={blog.image}
                    alt={blog.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                    <span className="text-gray-500 text-sm">No Image</span>
                  </div>
                )}
              </div>

              {/* CONTENT */}
              <div className="p-4 flex-1 flex flex-col justify-between group">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1 truncate">
                    {blog.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-2 line-clamp-3">
                    {blog.content}
                  </p>
                </div>

                <div className="mt-auto">
                  <p className="text-xs text-gray-500 mb-1">By {blog.author}</p>
                </div>

                {/* BUTTONS: slide-up on hover */}
                <div
                  className="absolute bottom-0 left-0 w-full flex justify-end gap-2 transform translate-y-full opacity-0 
             bg-white bg-opacity-70 py-2 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 px-4"
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleEditClick(blog);
                    }}
                    className="flex items-center justify-center transition 
             text-primaryBtnColor hover:text-primaryBtnHoverColor
             shadow hover:shadow-md"
                  >
                    <FiEdit size={20} />
                  </button>

                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleOpenDeleteModal(blog._id);
                    }}
                    className="flex items-center justify-center transition 
             text-red-800 hover:text-red-700
             shadow hover:shadow-md"
                  >
                    <FiTrash2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION */}
      <Pagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalPages={totalPages}
      />

      {/* DELETE CONFIRMATION MODAL */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        onClose={handleCancelDelete}
        loading={isDeleting}
        itemName={"this Blog "}
      />

      {/* BLOG FORM MODAL */}
      <AdminBlogModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleBlogSubmit}
        initialData={editData}
        isEditMode={!!editData}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default AdminBlogs;
